import React, { useCallback, useEffect, useState } from "react";
import { Users, BadgeCheck, Crown, Ban, ShieldOff, Loader2, Search, SlidersHorizontal, Mail, Trash2, Send, Coins, MapPinned, EyeOff, Eye } from "lucide-react";
import { CURRENCIES } from "../../utils/currency";
import api from "../../api";
import { useEnabledCategoryNames } from "../../hooks/useEnabledCategories";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

const LANG_NAMES = { en: "English", es: "Español", fr: "Français", pt: "Português", de: "Deutsch" };

const Pill = ({ tone = "muted", children, testid }) => {
  const tones = {
    muted: "bg-secondary text-muted-foreground",
    green: "bg-[#16a34a] text-white",
    amber: "bg-amber-100 text-amber-700",
    rose: "bg-rose-500 text-white",
  };
  return <span data-testid={testid} className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${tones[tone]}`}>{children}</span>;
};

// Places Match: does a real Google listing with this shop's name sit on each pin it dropped?
// A signal for judging a new shop, never a gate — plenty of honest shops have no listing.
export const placesLabel = (pm) => {
  if (!pm || !pm.pins) return "Google listing: not checked yet";
  const cityLevel = (pm.results || []).some((r) => r.cityLevel);
  const best = (pm.results || [])[0] || {};
  if (pm.pins === 1) {
    const where = cityLevel ? " (town-wide check — no pin saved yet)" : "";
    if (best.status === "matched") return `Google listing matches the pin · ${best.listing}${where}`;
    if (best.status === "nearby") return `Listing found ${best.distanceM}m from the pin · ${best.listing}${where}`;
    if (best.status === "far") return `"${best.listing}" exists, but ${(best.distanceM / 1000).toFixed(1)}km from this pin${where}`;
    if (best.status === "unknown") return "Google listing: could not check";
    return `No Google listing found for this name${where}`;
  }
  return `Google listing: ${pm.matched} of ${pm.pins} pins matched${pm.nearby ? ` · ${pm.nearby} nearby` : ""}${pm.far ? ` · ${pm.far} far off` : ""}${pm.noMatch ? ` · ${pm.noMatch} unmatched` : ""}`;
};

export const placesTone = (pm) => {
  if (!pm || !pm.pins) return "text-muted-foreground";
  if (pm.matched === pm.pins) return "text-[#16a34a] font-semibold";
  if (pm.matched || pm.nearby || pm.far) return "text-amber-700 font-semibold";
  return "text-rose-600 font-semibold";
};

export const AdminAccounts = () => {
  const { toast } = useToast();
  const catNames = useEnabledCategoryNames();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("business");
  const [lang, setLang] = useState("all");
  const [busy, setBusy] = useState("");
  const [suspendFor, setSuspendFor] = useState(null);
  const [mode, setMode] = useState("soft");
  const [days, setDays] = useState("0");
  const [reason, setReason] = useState("");
  const [proFor, setProFor] = useState(null);
  const [duration, setDuration] = useState("30d");
  const [proReason, setProReason] = useState("");
  const [limitsFor, setLimitsFor] = useState(null);
  const [emailFor, setEmailFor] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [limDeals, setLimDeals] = useState("");
  const [limBranches, setLimBranches] = useState("");
  const [grantCredits, setGrantCredits] = useState("");
  const [grantAi, setGrantAi] = useState("");
  const [deleteFor, setDeleteFor] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [messageFor, setMessageFor] = useState(null);
  const [msgSubject, setMsgSubject] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [currencyFor, setCurrencyFor] = useState(null);
  const [newCurrency, setNewCurrency] = useState("USD");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (role !== "all") params.set("role", role);
      if (lang !== "all") params.set("lang", lang);
      const r = await api.get(`/admin/users?${params.toString()}`);
      setRows(r.data);
    } catch { /* ignore */ }
  }, [q, role, lang]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const act = async (id, path, body, msg) => {
    setBusy(id + path);
    try {
      await api.post(`/admin/users/${id}/${path}`, body || {});
      toast({ title: msg });
      await load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Action failed" });
    } finally { setBusy(""); }
  };

  const doSuspend = async () => {
    const u = suspendFor;
    setSuspendFor(null);
    await act(u.id, "suspend", { mode, reason, days: Number(days) || null },
      mode === "hard" ? "Account banned" : "Account restricted");
    setReason(""); setDays("0"); setMode("soft");
  };

  const doGrantPro = async () => {
    const u = proFor;
    setProFor(null);
    await act(u.id, "grant-pro", { duration, reason: proReason }, "Pro granted");
    setProReason("");
  };

  const openLimits = (u) => {
    setLimitsFor(u);
    setLimDeals(u.limitOverrides?.deals ? String(u.limitOverrides.deals) : "");
    setLimBranches(u.limitOverrides?.branches ? String(u.limitOverrides.branches) : "");
    setGrantCredits("");
    setGrantAi(u.aiDailyLimit != null ? String(u.aiDailyLimit) : "");
  };

  const doLimits = async () => {
    const u = limitsFor;
    setLimitsFor(null);
    await act(u.id, "limits", { deals: Number(limDeals) || null, branches: Number(limBranches) || null },
      "Limits updated");
    const credits = Number(grantCredits) || 0;
    const ai = grantAi === "" ? null : Number(grantAi);
    if (credits || ai !== null) {
      await act(u.id, "grant", { dealCredits: credits || null, aiDailyLimit: ai },
        credits ? `${credits} extra deals added` : "AI draft limit set");
    }
  };

  const doEmail = async () => {
    const u = emailFor;
    setEmailFor(null);
    await act(u.id, "email", { email: newEmail.trim().toLowerCase() }, "Sign-in email changed");
  };

  const doDelete = async () => {
    const u = deleteFor;
    setDeleteFor(null);
    await act(u.id, "delete", { reason: delReason.trim() }, "Account deleted and the owner told");
    setDelReason(""); setDelConfirm("");
  };

  const doCurrency = async () => {
    const u = currencyFor;
    setCurrencyFor(null);
    await act(u.id, "currency", { currency: newCurrency }, `${u.businessName || u.name} now prices in ${newCurrency}`);
  };

  const doMessage = async () => {
    const u = messageFor;
    setMessageFor(null);
    await act(u.id, "message", { subject: msgSubject.trim(), message: msgBody.trim() },
      `Emailed ${u.email}`);
    setMsgSubject(""); setMsgBody("");
  };

  return (
    <div className="space-y-5">
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-accounts">
      <h2 className="font-bold text-foreground flex items-center gap-2"><Users size={18} className="text-[#16a34a]" /> Accounts</h2>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">Verify businesses, gift Pro, and suspend bad actors.</p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or business"
            data-testid="admin-user-search"
            className="w-full bg-secondary rounded-xl pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#16a34a]/40" />
        </div>
        <div className="inline-flex items-center gap-1 bg-secondary rounded-full p-1">
          {["business", "shopper", "all"].map((r) => (
            <button key={r} onClick={() => setRole(r)} data-testid={`admin-role-${r}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize transition-all ${role === r ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>{r}</button>
          ))}
        </div>
        <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="admin-lang-filter"
          className="bg-secondary rounded-xl px-3 py-2 text-xs font-bold text-foreground outline-none focus:ring-2 focus:ring-[#16a34a]/40">
          <option value="all">All languages</option>
          {Object.entries(LANG_NAMES).map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </select>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No accounts match.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((u) => (
            <div key={u.id} className="border border-border rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid={`account-row-${u.id}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-foreground truncate">{u.businessName || u.name}</span>
                  {u.verified && <Pill tone="green" testid={`verified-pill-${u.id}`}>Verified</Pill>}
                  {u.plan === "pro" && <Pill tone="amber" testid={`pro-pill-${u.id}`}>{u.planLifetime ? "Pro · lifetime" : "Pro"}</Pill>}
                  {u.plan === "business_plus" && <Pill tone="green" testid={`plus-pill-${u.id}`}>Elite</Pill>}
                  {(u.limitOverrides?.deals || u.limitOverrides?.branches) && (
                    <Pill tone="amber" testid={`limits-pill-${u.id}`}>
                      Raised{u.limitOverrides.deals ? ` · ${u.limitOverrides.deals}/mo` : ""}{u.limitOverrides.branches ? ` · ${u.limitOverrides.branches} pins` : ""}
                    </Pill>
                  )}
                  {u.dealCredits > 0 && (
                    <Pill tone="green" testid={`credits-pill-${u.id}`}>{u.dealCredits} extra deals</Pill>
                  )}
                  {u.suspended && <Pill tone="rose" testid={`suspended-pill-${u.id}`}>{u.suspension?.mode === "hard" ? "Banned" : "Restricted"}</Pill>}
                  {u.listingHidden && <Pill tone="amber" testid={`delisted-pill-${u.id}`}>Hidden from listing</Pill>}
                  {u.role === "shopper" && <Pill>Shopper</Pill>}
                  {u.role !== "shopper" && u.role !== "admin" && !u.verified && <Pill testid={`independent-pill-${u.id}`}>Independent seller</Pill>}
                  {u.role === "business" && !u.emailVerified && <Pill tone="rose" testid={`email-unverified-pill-${u.id}`}>Email unconfirmed</Pill>}
                  {u.role === "business" && u.emailVerified && u.domainVerified && (
                    <Pill tone="green" testid={`domain-verified-pill-${u.id}`}>Domain email verified{u.verifiedDomain ? ` · ${u.verifiedDomain}` : ""}</Pill>
                  )}
                  {u.role === "admin" && <Pill tone="green">Admin</Pill>}
                  {u.role === "business" && u.effectivePlan === "free" && <Pill testid={`free-pill-${u.id}`}>Free</Pill>}
                  {u.trust === "trusted" && <Pill tone="green" testid={`trust-pill-${u.id}`}>Trusted</Pill>}
                  {u.trust === "watch" && <Pill tone="amber" testid={`trust-pill-${u.id}`}>On watch{u.strikes ? ` · ${u.strikes} strikes` : ""}</Pill>}
                  {u.lang && u.lang !== "en" && <Pill testid={`lang-pill-${u.id}`}>{LANG_NAMES[u.lang] || u.lang}</Pill>}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {u.email} · {u.role === "shopper"
                    ? `${u.grabs || 0} deal${u.grabs === 1 ? "" : "s"} grabbed`
                    : `${u.deals} deal${u.deals === 1 ? "" : "s"}`}{u.location ? ` · ${u.location}` : ""}
                </p>
                <p className="text-xs text-muted-foreground truncate" data-testid={`account-contact-${u.id}`}>
                  {[u.phone && `☎ ${u.phone}`, u.whatsapp && `WhatsApp ${u.whatsapp}`, u.category, u.country,
                    u.currency, u.lang ? `reads ${LANG_NAMES[u.lang] || u.lang}` : "",
                    u.createdAt ? `joined ${String(u.createdAt).slice(0, 10)}` : ""]
                    .filter(Boolean).join(" · ") || "No contact details on file"}
                </p>
                {u.role === "business" && (
                  <p className="text-xs mt-0.5 flex items-center gap-1.5 flex-wrap" data-testid={`account-places-${u.id}`}>
                    <MapPinned size={12} className="text-muted-foreground shrink-0" />
                    <span className={placesTone(u.placesMatch)}>{placesLabel(u.placesMatch)}</span>
                    <button onClick={() => act(u.id, "places-match", {}, "Listing check re-run")}
                      disabled={busy === u.id + "places-match"} data-testid={`places-recheck-${u.id}`}
                      className="text-[11px] font-semibold text-muted-foreground hover:text-[#16a34a] underline decoration-dotted transition-colors">
                      {busy === u.id + "places-match" ? "checking…" : "re-check"}
                    </button>
                  </p>
                )}
                <p className="text-xs text-muted-foreground" data-testid={`account-consent-${u.id}`}>
                  {u.termsAcceptedAt
                    ? `Agreed to Terms & Privacy on ${String(u.termsAcceptedAt).slice(0, 10)}`
                    : "No consent on file — account predates the signup checkbox"}
                </p>
                {u.suspended && u.suspension?.reason && (
                  <p className="text-xs text-rose-600 mt-0.5">Reason: {u.suspension.reason}{u.suspension.until ? ` · lifts ${String(u.suspension.until).slice(0, 10)}` : " · no end date"}</p>
                )}
                {u.plan === "pro" && u.planGrant && (
                  <p className="text-xs text-muted-foreground mt-0.5">Comped by {u.planGrant.by}{u.planExpiresAt ? ` · until ${String(u.planExpiresAt).slice(0, 10)}` : ""}</p>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {u.businessName && (
                  <button onClick={() => act(u.id, "verify", { verified: !u.verified }, u.verified ? "Verification removed" : "Business verified")}
                    disabled={busy === u.id + "verify"} data-testid={`verify-btn-${u.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:border-[#16a34a] rounded-xl px-3 py-2 transition-colors">
                    {busy === u.id + "verify" ? <Loader2 className="animate-spin" size={13} /> : <BadgeCheck size={13} className={u.verified ? "text-muted-foreground" : "text-[#16a34a]"} />}
                    {u.verified ? "Unverify" : "Verify"}
                  </button>
                )}
                {u.role === "business" && u.effectivePlan !== "business_plus" && (u.effectivePlan === "pro" ? (
                  <button onClick={() => act(u.id, "revoke-pro", {}, "Pro removed")} disabled={busy === u.id + "revoke-pro"}
                    data-testid={`revoke-pro-btn-${u.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:bg-secondary rounded-xl px-3 py-2 transition-colors">
                    {busy === u.id + "revoke-pro" ? <Loader2 className="animate-spin" size={13} /> : <Crown size={13} />} Remove Pro
                  </button>
                ) : (
                  <button onClick={() => { setProFor(u); setDuration("30d"); }} data-testid={`grant-pro-btn-${u.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-3 py-2 transition-colors">
                    <Crown size={13} /> Grant Pro
                  </button>
                ))}
                {u.role === "business" && (
                  <select value={u.category || ""} data-testid={`category-select-${u.id}`}
                    onChange={(e) => act(u.id, "category", { category: e.target.value }, `Filed under ${e.target.value}`)}
                    className="text-xs font-semibold border border-border rounded-xl px-2 py-2 bg-card outline-none">
                    {!u.category && <option value="">No category</option>}
                    {catNames.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}
                {u.businessName && (
                  <select value={u.trust || "normal"} data-testid={`trust-select-${u.id}`}
                    onChange={(e) => act(u.id, "trust", { level: e.target.value },
                      e.target.value === "watch" ? "Deals will need approval" : e.target.value === "trusted" ? "Shop is now trusted" : "Back to normal checks")}
                    className="text-xs font-semibold border border-border rounded-xl px-2 py-2 bg-card outline-none">
                    <option value="trusted">Trusted — skip checks</option>
                    <option value="normal">Normal checks</option>
                    <option value="watch">On watch — approve first</option>
                  </select>
                )}
                {u.plan === "business_plus" && (
                  <button onClick={() => openLimits(u)} data-testid={`limits-btn-${u.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:border-[#16a34a] rounded-xl px-3 py-2 transition-colors">
                    <SlidersHorizontal size={13} /> Limits
                  </button>
                )}
                <button onClick={() => { setEmailFor(u); setNewEmail(u.email || ""); }} data-testid={`change-email-btn-${u.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:border-[#16a34a] rounded-xl px-3 py-2 transition-colors">
                  <Mail size={13} /> Email
                </button>
                <button onClick={() => { setCurrencyFor(u); setNewCurrency(u.currency || "USD"); }} data-testid={`currency-btn-${u.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:border-[#16a34a] rounded-xl px-3 py-2 transition-colors">
                  <Coins size={13} /> Currency
                </button>
                <button onClick={() => { setMessageFor(u); setMsgSubject(""); setMsgBody(""); }} data-testid={`message-btn-${u.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:border-[#16a34a] rounded-xl px-3 py-2 transition-colors">
                  <Send size={13} /> Message
                </button>
                {u.role !== "admin" && (u.suspended ? (
                  <button onClick={() => act(u.id, "unsuspend", {}, "Suspension lifted")} disabled={busy === u.id + "unsuspend"}
                    data-testid={`unsuspend-btn-${u.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#16a34a] hover:bg-[#15803d] text-white rounded-xl px-3 py-2 transition-colors">
                    {busy === u.id + "unsuspend" ? <Loader2 className="animate-spin" size={13} /> : <ShieldOff size={13} />} Lift
                  </button>
                ) : (
                  <button onClick={() => { setSuspendFor(u); setMode("soft"); setDays("0"); setReason(""); }} data-testid={`suspend-btn-${u.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-rose-500 hover:bg-rose-600 text-white rounded-xl px-3 py-2 transition-colors">
                    <Ban size={13} /> Suspend
                  </button>
                ))}
                {u.role !== "admin" && u.role !== "shopper" && (
                  u.listingHidden ? (
                    <button onClick={() => act(u.id, "relist", {}, "Shop is visible to shoppers again")}
                      disabled={busy === u.id + "relist"} data-testid={`relist-btn-${u.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#16a34a] hover:bg-[#15803d] text-white rounded-xl px-3 py-2 transition-colors">
                      {busy === u.id + "relist" ? <Loader2 className="animate-spin" size={13} /> : <Eye size={13} />} Show in listing
                    </button>
                  ) : (
                    <button onClick={() => act(u.id, "delist", {}, "Shop hidden from all public listings")}
                      disabled={busy === u.id + "delist"} data-testid={`delist-btn-${u.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold border border-amber-500/50 text-amber-600 hover:bg-amber-500/10 rounded-xl px-3 py-2 transition-colors">
                      {busy === u.id + "delist" ? <Loader2 className="animate-spin" size={13} /> : <EyeOff size={13} />} Hide from listing
                    </button>
                  )
                )}
                {u.role !== "admin" && (
                  <button onClick={() => { setDeleteFor(u); setDelReason(""); setDelConfirm(""); }} data-testid={`delete-account-btn-${u.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-rose-500/50 text-rose-600 hover:bg-rose-500/10 rounded-xl px-3 py-2 transition-colors">
                    <Trash2 size={13} /> Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!currencyFor} onOpenChange={(o) => !o && setCurrencyFor(null)}>
        <DialogContent data-testid="currency-dialog">
          <DialogHeader><DialogTitle>Currency for {currencyFor?.businessName || currencyFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Changes what their prices are shown in from now on. Existing deals keep the currency they
              were posted with.
            </p>
            <select value={newCurrency} onChange={(e) => setNewCurrency(e.target.value)} data-testid="currency-select"
              className="w-full bg-secondary rounded-xl px-3 py-2.5 text-sm font-semibold text-foreground outline-none">
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
          <DialogFooter>
            <button onClick={() => setCurrencyFor(null)} className="text-sm font-semibold px-4 py-2 rounded-xl border border-border hover:bg-secondary">Cancel</button>
            <button onClick={doCurrency} data-testid="currency-save"
              className="text-sm font-bold px-4 py-2 rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white">Save currency</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!messageFor} onOpenChange={(o) => !o && setMessageFor(null)}>
        <DialogContent data-testid="message-user-dialog">
          <DialogHeader><DialogTitle>Message {messageFor?.businessName || messageFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Goes straight to {messageFor?.email} from LytePress. Their reply comes back to our inbox, and a copy is
              kept under Inbox → Contact.
            </p>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Subject</label>
              <input value={msgSubject} onChange={(e) => setMsgSubject(e.target.value)} data-testid="message-user-subject"
                placeholder="e.g. Your shop is ready to go live"
                className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Message</label>
              <textarea value={msgBody} onChange={(e) => setMsgBody(e.target.value)} rows={5} data-testid="message-user-body"
                placeholder="Write it like a person, not a system."
                className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none resize-none" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setMessageFor(null)} className="text-sm font-semibold px-4 py-2 rounded-xl border border-border hover:bg-secondary">Cancel</button>
            <button onClick={doMessage} disabled={msgSubject.trim().length < 2 || msgBody.trim().length < 2}
              data-testid="message-user-send"
              className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-xl bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white">
              <Send size={14} /> Send email
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle>Delete {deleteFor?.businessName || deleteFor?.name}?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This removes the account, {deleteFor?.deals || 0} deal(s) and their photos, saved codes and sessions —
              live deals included. Reviews they wrote stay as “Deleted user”. It cannot be undone.
            </p>
            {(deleteFor?.effectivePlan && deleteFor.effectivePlan !== "free") || deleteFor?.hasBilling ? (
              <div data-testid="delete-user-override-warning"
                className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 space-y-1">
                <p className="font-bold">⚠️ You are overriding active-account safety checks.</p>
                {deleteFor?.effectivePlan && deleteFor.effectivePlan !== "free" && (
                  <p>Active <span className="font-semibold capitalize">{deleteFor.effectivePlan}</span> plan
                    {deleteFor.planLifetime ? " (lifetime)" : deleteFor.planExpiresAt ? ` — paid term runs until ${String(deleteFor.planExpiresAt).slice(0, 10)}` : ""}.</p>
                )}
                {deleteFor?.hasBilling
                  ? <p>Their Stripe subscription will be <span className="font-semibold">cancelled now</span> so the card is never charged again.</p>
                  : <p>No active Stripe billing on file.</p>}
              </div>
            ) : null}
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Reason (emailed to them)</label>
              <textarea value={delReason} onChange={(e) => setDelReason(e.target.value)} rows={2} data-testid="delete-user-reason"
                placeholder="e.g. Fraudulent listings after two warnings"
                className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none resize-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Type DELETE to confirm</label>
              <input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} data-testid="delete-user-confirm-input"
                className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDeleteFor(null)} className="text-sm font-semibold px-4 py-2 rounded-xl border border-border hover:bg-secondary">Cancel</button>
            <button onClick={doDelete} disabled={delConfirm.trim().toUpperCase() !== "DELETE"} data-testid="delete-user-confirm"
              className="text-sm font-bold px-4 py-2 rounded-xl bg-rose-500 hover:bg-rose-600 disabled:opacity-60 text-white">Delete account</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!suspendFor} onOpenChange={(o) => !o && setSuspendFor(null)}>
        <DialogContent data-testid="suspend-dialog">
          <DialogHeader><DialogTitle>Suspend {suspendFor?.businessName || suspendFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              {[
                { v: "soft", t: "Restrict (soft)", d: "Can still sign in. Cannot post or edit deals; live deals are hidden." },
                { v: "hard", t: "Ban (hard)", d: "Sign-in blocked and every session ended. Live deals hidden." },
              ].map((o) => (
                <button key={o.v} onClick={() => setMode(o.v)} data-testid={`suspend-mode-${o.v}`}
                  className={`w-full text-left rounded-xl border p-3 transition-colors ${mode === o.v ? "border-[#16a34a] bg-[#16a34a]/5" : "border-border hover:bg-secondary"}`}>
                  <p className="text-sm font-bold text-foreground">{o.t}</p>
                  <p className="text-xs text-muted-foreground">{o.d}</p>
                </button>
              ))}
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">How long?</label>
              <select value={days} onChange={(e) => setDays(e.target.value)} data-testid="suspend-days"
                className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none">
                <option value="0">Until I lift it</option>
                <option value="3">3 days</option>
                <option value="7">7 days</option>
                <option value="30">30 days</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Reason (emailed to them)</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="suspend-reason"
                placeholder="e.g. Repeated fake discounts after warnings"
                className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none resize-none" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setSuspendFor(null)} className="text-sm font-semibold px-4 py-2 rounded-xl border border-border hover:bg-secondary">Cancel</button>
            <button onClick={doSuspend} data-testid="suspend-confirm"
              className="text-sm font-bold px-4 py-2 rounded-xl bg-rose-500 hover:bg-rose-600 text-white">Suspend account</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!proFor} onOpenChange={(o) => !o && setProFor(null)}>
        <DialogContent data-testid="grant-pro-dialog">
          <DialogHeader><DialogTitle>Grant Pro to {proFor?.businessName || proFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Duration</label>
              <select value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="grant-pro-duration"
                className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none">
                <option value="30d">30 days</option>
                <option value="1y">1 year</option>
                <option value="lifetime">Lifetime (founding member)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Note (shown in their email)</label>
              <input value={proReason} onChange={(e) => setProReason(e.target.value)} data-testid="grant-pro-reason"
                placeholder="e.g. Founding member — thanks for being first!"
                className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setProFor(null)} className="text-sm font-semibold px-4 py-2 rounded-xl border border-border hover:bg-secondary">Cancel</button>
            <button onClick={doGrantPro} data-testid="grant-pro-confirm"
              className="text-sm font-bold px-4 py-2 rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white">Grant Pro</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!limitsFor} onOpenChange={(o) => !o && setLimitsFor(null)}>
        <DialogContent data-testid="limits-dialog">
          <DialogHeader><DialogTitle>Raise limits for {limitsFor?.businessName || limitsFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              This shop's plan covers {limitsFor?.planLimits?.deals ?? 4} deals a month and {limitsFor?.planLimits?.branches ?? 1} pinned
              locations. Set either one by hand for this account — leave a field empty to put it back to the plan's.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Deals per month</label>
                <input value={limDeals} onChange={(e) => setLimDeals(e.target.value.replace(/\D/g, ""))} data-testid="limits-deals"
                  placeholder={String(limitsFor?.planLimits?.deals ?? 4)} inputMode="numeric"
                  className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Pinned locations</label>
                <input value={limBranches} onChange={(e) => setLimBranches(e.target.value.replace(/\D/g, ""))} data-testid="limits-branches"
                  placeholder={String(limitsFor?.planLimits?.branches ?? 1)} inputMode="numeric"
                  className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none" />
              </div>
            </div>
            <div className="rounded-xl border border-border p-3 space-y-3">
              <p className="text-xs font-bold text-foreground">Give this shop extras, free of charge</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground">Add extra deals (credits)</label>
                  <input value={grantCredits} onChange={(e) => setGrantCredits(e.target.value.replace(/[^\d-]/g, ""))}
                    data-testid="grant-credits" placeholder="e.g. 10" inputMode="numeric"
                    className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none" />
                  <p className="text-[11px] text-muted-foreground mt-1">Holds {limitsFor?.dealCredits ?? 0} now. Negative takes some back.</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground">AI drafts per day</label>
                  <input value={grantAi} onChange={(e) => setGrantAi(e.target.value.replace(/\D/g, ""))}
                    data-testid="grant-ai" placeholder="20 (default)" inputMode="numeric"
                    className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none" />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {limitsFor?.aiDailyLimit != null ? `Set to ${limitsFor.aiDailyLimit} for this shop.` : "Using the standard 20 a day."}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setLimitsFor(null)} className="text-sm font-semibold px-4 py-2 rounded-xl border border-border hover:bg-secondary">Cancel</button>
            <button onClick={doLimits} data-testid="limits-confirm"
              className="text-sm font-bold px-4 py-2 rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white">Save limits</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!emailFor} onOpenChange={(o) => !o && setEmailFor(null)}>
        <DialogContent data-testid="admin-email-dialog">
          <DialogHeader><DialogTitle>Change sign-in email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              For an owner who can't receive mail at {emailFor?.email}. The change is instant, every device is
              signed out, both addresses are notified and it's written to the admin log.
            </p>
            <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="admin-email-input"
              type="email" placeholder="owner@theirshop.com" autoComplete="off"
              className="w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none" />
          </div>
          <DialogFooter>
            <button onClick={() => setEmailFor(null)} className="text-sm font-semibold px-4 py-2 rounded-xl border border-border hover:bg-secondary">Cancel</button>
            <button onClick={doEmail} data-testid="admin-email-confirm"
              className="text-sm font-bold px-4 py-2 rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white">Change email</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
};

export default AdminAccounts;
